import { useEffect, useState } from "react";
import {
  Banknote,
  CalendarCheck,
  CircleDollarSign,
  Download,
  HandCoins,
  ReceiptText,
  RotateCcw,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/data";
import { Input, Label, Select } from "@/components/ui/form";
import { Button, buttonVariants } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn, formatMoney } from "@/lib/utils";

interface Location {
  id: number;
  name: string;
  currency: string;
}

interface ReportSummary {
  location: Location;
  period: { from: string; to: string; endExclusive: true };
  kpis: {
    settledGrossMinor: number;
    refundsMinor: number;
    netCollectedMinor: number;
    outstandingMinor: number;
    tipsMinor: number;
    completedBookings: number;
  };
  byPaymentMethod: Array<{ method: string; grossMinor: number; refundsMinor: number; netMinor: number }>;
  byService: Array<{
    serviceId: number | null;
    serviceName: string;
    units: number;
    bookings: number;
    completedBookings: number;
    grossMinor: number;
    refundsMinor: number;
    netMinor: number;
  }>;
  membershipRevenue: Array<{ membershipId: number; planName: string; currency: string; grossMinor: number; refundsMinor: number; netMinor: number }>;
  membershipTotals: Array<{ currency: string; grossMinor: number; refundsMinor: number; netMinor: number }>;
  reconciliation: Array<{ currency: string; bookingNetMinor: number; membershipNetMinor: number; internalNetMinor: number; settlementNetMinor: number; differenceMinor: number; matchedLines: number; varianceLines: number; unmatchedLines: number }>;
}

const methodLabels: Record<string, string> = {
  CASH: "Cash",
  CARD_OFFLINE: "Card",
  BANK_TRANSFER: "Bank transfer",
  WALLET: "Wallet",
  OTHER: "Other",
};

export function ReportsPage() {
  const initialDates = defaultDates();
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [from, setFrom] = useState(initialDates.from);
  const [to, setTo] = useState(initialDates.to);
  const [report, setReport] = useState<ReportSummary | null>(null);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    api.get<Location[]>("/api/v1/locations", controller.signal)
      .then((rows) => {
        setLocations(rows);
        setLocationId((current) => current ?? rows[0]?.id ?? null);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(errorMessage(reason, "Could not load locations."));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingLocations(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!locationId || !from || !to || to <= from) {
      setReport(null);
      return;
    }
    const controller = new AbortController();
    const params = reportParams(locationId, from, to);
    setLoading(true);
    setError(null);
    api.get<ReportSummary>(`/api/v1/reports/summary?${params}`, controller.signal)
      .then(setReport)
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setReport(null);
          setError(errorMessage(reason, "Could not load this report."));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [locationId, from, to, refreshKey]);

  const invalidRange = Boolean(from && to && to <= from);
  const params = locationId ? reportParams(locationId, from, to) : "";
  const currency = report?.location.currency ?? locations.find((location) => location.id === locationId)?.currency ?? "USD";
  const empty = report && report.kpis.completedBookings === 0 && report.kpis.netCollectedMinor === 0 && report.kpis.outstandingMinor === 0 && report.membershipTotals.length === 0 && report.reconciliation.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm font-semibold text-primary">Financial reporting</p>
          <h1 className="page-heading mt-1">Collections and service mix</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Settled payments and refunds use booking-currency ledger amounts. The end date is exclusive.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            className={cn(buttonVariants({ variant: "outline", size: "sm" }), (!locationId || invalidRange) && "pointer-events-none opacity-50")}
            href={`/api/v1/reports/bookings.csv?${params}`}
          >
            <Download className="h-4 w-4" /> Bookings CSV
          </a>
          <a
            className={cn(buttonVariants({ variant: "outline", size: "sm" }), (!locationId || invalidRange) && "pointer-events-none opacity-50")}
            href={`/api/v1/reports/service-mix.csv?${params}`}
          >
            <Download className="h-4 w-4" /> Service mix CSV
          </a>
          <a
            className={cn(buttonVariants({ variant: "outline", size: "sm" }), !locationId && "pointer-events-none opacity-50")}
            href={`/api/v1/reports/customers.csv?locationId=${locationId ?? ""}`}
          >
            <Download className="h-4 w-4" /> Customers CSV
          </a>
          <a
            className={cn(buttonVariants({ variant: "outline", size: "sm" }), (!locationId || invalidRange) && "pointer-events-none opacity-50")}
            href={`/api/v1/reports/membership-revenue.csv?${params}`}
          >
            <Download className="h-4 w-4" /> Membership CSV
          </a>
          <a
            className={cn(buttonVariants({ variant: "outline", size: "sm" }), (!locationId || invalidRange) && "pointer-events-none opacity-50")}
            href={`/api/v1/reports/reconciliation.csv?${params}`}
          >
            <Download className="h-4 w-4" /> Reconciliation CSV
          </a>
        </div>
      </div>

      <Card>
        <CardContent className="grid gap-4 pt-5 sm:grid-cols-3 md:pt-6">
          <div className="space-y-2">
            <Label htmlFor="report-location">Location</Label>
            <Select id="report-location" value={locationId ?? ""} disabled={loadingLocations} onChange={(event) => setLocationId(Number(event.target.value))}>
              {locations.length === 0 && <option value="">{loadingLocations ? "Loading locations..." : "No locations"}</option>}
              {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-from">Start date</Label>
            <Input id="report-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-to">End date (exclusive)</Label>
            <Input id="report-to" type="date" value={to} min={from ? addDays(from, 1) : undefined} onChange={(event) => setTo(event.target.value)} />
          </div>
          {invalidRange && <p className="text-sm text-destructive sm:col-span-3">End date must be after start date.</p>}
        </CardContent>
      </Card>

      {error && (
        <div role="alert" className="flex flex-col gap-3 rounded-xl border border-destructive/30 bg-destructive/[0.06] p-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={() => setRefreshKey((key) => key + 1)}><RotateCcw className="h-4 w-4" /> Try again</Button>
        </div>
      )}

      {loading && !report && <ReportSkeleton />}

      {!loading && empty && (
        <Card className="border-dashed">
          <CardContent className="flex min-h-48 flex-col items-center justify-center text-center">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-muted text-muted-foreground"><ReceiptText className="h-5 w-5" /></span>
            <p className="mt-3 font-semibold">No report activity</p>
            <p className="mt-1 text-sm text-muted-foreground">No balances, settled ledger entries, or completed bookings match this date range.</p>
          </CardContent>
        </Card>
      )}

      {report && !empty && (
        <div className={cn("space-y-6 transition-opacity", loading && "opacity-60")} aria-busy={loading}>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <Kpi icon={CircleDollarSign} label="Settled gross" value={formatMoney(report.kpis.settledGrossMinor, currency)} />
            <Kpi icon={RotateCcw} label="Refunds" value={formatMoney(report.kpis.refundsMinor, currency)} tone="negative" />
            <Kpi icon={Banknote} label="Net collected" value={formatMoney(report.kpis.netCollectedMinor, currency)} tone="positive" />
            <Kpi icon={ReceiptText} label="Outstanding" value={formatMoney(report.kpis.outstandingMinor, currency)} />
            <Kpi icon={HandCoins} label="Tips" value={formatMoney(report.kpis.tipsMinor, currency)} />
            <Kpi icon={CalendarCheck} label="Completed" value={report.kpis.completedBookings.toLocaleString()} />
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
            <Card>
              <CardHeader>
                <CardTitle>Payment methods</CardTitle>
                <CardDescription>Refunds are attributed to the original payment method.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Method</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Refunds</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {report.byPaymentMethod.map((row) => (
                      <TableRow key={row.method}>
                        <TableCell className="font-medium">{methodLabels[row.method] ?? titleCase(row.method)}</TableCell>
                        <MoneyCell value={row.grossMinor} currency={currency} />
                        <MoneyCell value={row.refundsMinor} currency={currency} muted />
                        <MoneyCell value={row.netMinor} currency={currency} strong />
                      </TableRow>
                    ))}
                    {report.byPaymentMethod.length === 0 && <EmptyRow columns={4} message="No settled payments in this period." />}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Services</CardTitle>
                <CardDescription>Non-tip collections allocated by each service line’s value.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow><TableHead>Service</TableHead><TableHead className="text-right">Units</TableHead><TableHead className="text-right">Completed</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Refunds</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {report.byService.map((row) => (
                      <TableRow key={`${row.serviceId ?? "deleted"}-${row.serviceName}`}>
                        <TableCell><p className="font-medium">{row.serviceName}</p><p className="text-xs text-muted-foreground">{row.bookings} booking{row.bookings === 1 ? "" : "s"}</p></TableCell>
                        <TableCell className="text-right tabular-nums">{row.units.toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums">{row.completedBookings.toLocaleString()}</TableCell>
                        <MoneyCell value={row.grossMinor} currency={currency} />
                        <MoneyCell value={row.refundsMinor} currency={currency} muted />
                        <MoneyCell value={row.netMinor} currency={currency} strong />
                      </TableRow>
                    ))}
                    {report.byService.length === 0 && <EmptyRow columns={6} message="No service lines match this period." />}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Membership cash revenue</CardTitle><CardDescription>Recognized when charges settle or refunds are recorded, not by invoice period.</CardDescription></CardHeader>
              <CardContent><Table><TableHeader><TableRow><TableHead>Plan</TableHead><TableHead>Currency</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Refunds</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader><TableBody>
                {report.membershipRevenue.map((row) => <TableRow key={`${row.membershipId}-${row.currency}`}><TableCell className="font-medium">{row.planName}<p className="text-xs font-normal text-muted-foreground">Membership #{row.membershipId}</p></TableCell><TableCell>{row.currency}</TableCell><MoneyCell value={row.grossMinor} currency={row.currency} /><MoneyCell value={row.refundsMinor} currency={row.currency} muted /><MoneyCell value={row.netMinor} currency={row.currency} strong /></TableRow>)}
                {report.membershipRevenue.length === 0 && <EmptyRow columns={5} message="No membership cash activity in this period." />}
              </TableBody></Table></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Settlement reconciliation</CardTitle><CardDescription>Every row is isolated by currency; amounts are never converted or combined.</CardDescription></CardHeader>
              <CardContent><Table><TableHeader><TableRow><TableHead>Currency</TableHead><TableHead className="text-right">Internal</TableHead><TableHead className="text-right">Settled</TableHead><TableHead className="text-right">Difference</TableHead><TableHead className="text-right">Exceptions</TableHead></TableRow></TableHeader><TableBody>
                {report.reconciliation.map((row) => <TableRow key={row.currency}><TableCell className="font-medium">{row.currency}</TableCell><MoneyCell value={row.internalNetMinor} currency={row.currency} /><MoneyCell value={row.settlementNetMinor} currency={row.currency} /><MoneyCell value={row.differenceMinor} currency={row.currency} strong /><TableCell className="text-right tabular-nums">{row.varianceLines + row.unmatchedLines}</TableCell></TableRow>)}
                {report.reconciliation.length === 0 && <EmptyRow columns={5} message="No cash or settlement activity in this period." />}
              </TableBody></Table></CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }: { icon: typeof Banknote; label: string; value: string; tone?: "positive" | "negative" }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">{label}</p>
          <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/[0.07] text-primary", tone === "positive" && "bg-emerald-50 text-emerald-700", tone === "negative" && "bg-rose-50 text-rose-700")}><Icon className="h-4 w-4" /></span>
        </div>
        <p className="mt-4 break-words text-xl font-semibold tracking-[-0.035em] tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function MoneyCell({ value, currency, muted, strong }: { value: number; currency: string; muted?: boolean; strong?: boolean }) {
  return <TableCell className={cn("text-right tabular-nums", muted && "text-muted-foreground", strong && "font-semibold")}>{formatMoney(value, currency)}</TableCell>;
}

function EmptyRow({ columns, message }: { columns: number; message: string }) {
  return <TableRow><TableCell colSpan={columns} className="py-8 text-center text-muted-foreground">{message}</TableCell></TableRow>;
}

function ReportSkeleton() {
  return (
    <div className="space-y-6" aria-label="Loading report">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, index) => <div key={index} className="h-32 animate-pulse rounded-xl border bg-muted/50" />)}
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="h-72 animate-pulse rounded-xl border bg-muted/50" />
        <div className="h-72 animate-pulse rounded-xl border bg-muted/50" />
      </div>
    </div>
  );
}

function defaultDates(): { from: string; to: string } {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const from = new Date(tomorrow);
  from.setDate(from.getDate() - 30);
  return { from: localDate(from), to: localDate(tomorrow) };
}

function addDays(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return localDate(date);
}

function localDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function reportParams(locationId: number, from: string, to: string): string {
  return new URLSearchParams({ locationId: String(locationId), from, to }).toString();
}

function titleCase(value: string): string {
  return value.toLowerCase().replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase());
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}
